import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import {
  MainHeader,
  MainHeaderWithButton,
  NavigationBreadcrumbItem,
  useBanners,
} from '@eposnow/ui-core';

import { UIContext } from '../../context/UIContext';
import Loader from '../Loader';
import { UserContext } from '../../context/UserContext';
import DesktopDataGrid from './DesktopDataGrid';
import MobileDataGrid from './MobileDataGrid';
import { PageMetadata } from './paginationHelpers';
import {
  DataGridConfigContext,
  DataGridConfigContextProvider,
} from '../../context/DataGridConfigContext';
import { DataGridContextProvider } from '../../context/DataGridContext';
import GridConfirmationDeleteModal from './GridConfirmationDeleteModal';
import GridSnackbarMessage from './GridSnackbarMessage';
import CreateEditSidebar from './CreateEditSidebar';
import { ColumnGroup } from './GridColumns';

const GenericDataGridAndSideFormContent = <T, TErrors>({
  unauthorized,
  pageDescription = '',
  nav,
  hasAddButton = true,
}: {
  unauthorized: boolean;
  pageDescription?: string;
  nav?: NavigationBreadcrumbItem[];
  hasAddButton?: boolean;
}) => {
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMobile, isTablet } = useContext(UIContext);
  const { fetchedFirstData, translationKey, addButtonNavigationPath } =
    useContext(DataGridConfigContext);
  const { banners, addBanner, hasBanner, removeBanner, BannersComponent } = useBanners();
  const { objectId } = useParams();
  const hasErrorBanner = hasBanner('deleteOrArchiveError');
  const handleAddClick = () => {
    if (objectId !== '0' && fetchedFirstData) {
      if (!addButtonNavigationPath.includes('http')) {
        navigate(addButtonNavigationPath);
      } else {
        window.location.href = addButtonNavigationPath;
      }
    }
  };

  return unauthorized ? (
    <Box
      sx={{
        marginLeft: 0,
        marginBottom: { xs: 0, sm: 2 },
        paddingX: { xs: 2, sm: 0 },
      }}
    >
      <MainHeader title={t(`screens.${translationKey}.title`)} nav={nav} theme={theme} />
      <Loader ariaBusy={unauthorized} />
    </Box>
  ) : (
    <>
      <BannersComponent
        banners={banners}
        theme={theme}
        openSideNav={false}
        locale={locale}
        isMobile={isMobile}
      />

      <Box
        sx={{
          marginBottom: { xs: pageDescription ? 2 : 0, sm: pageDescription ? 3 : 4 },
          marginLeft: 0,
          paddingX: { xs: 2, sm: 0 },
          ...(isMobile && {
            marginX: '-1rem',
          }),
        }}
      >
        <MainHeaderWithButton
          title={t(`screens.${translationKey}.title`)}
          nav={nav}
          isMobile={isMobile}
          theme={theme}
          styles={{
            ...(pageDescription && { paddingBottom: 0 }),
          }}
        >
          {hasAddButton && (
            <Button
              color="primary"
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                ...(isMobile && { padding: '6px 16px' }),
              }}
            >
              {isMobile ? t('actions.add') : t(`screens.${translationKey}.add`)}
            </Button>
          )}
        </MainHeaderWithButton>
        {pageDescription && (
          <Typography variant="body1" sx={{ paddingTop: '0.5rem' }}>
            {pageDescription}
          </Typography>
        )}
      </Box>

      <DataGridContextProvider addBanner={addBanner} removeBanner={removeBanner}>
        <>
          <GridSnackbarMessage />
          <Box>
            <Box
              sx={{
                height: {
                  xs: 'unset',
                  sm: `calc(100vh - 290px - ${hasErrorBanner ? '105px' : '0px'} - 64px)`,
                  md: `calc(100vh - 365.5px - ${hasErrorBanner ? '121px' : '0px'} - 64px)`,
                  lg: `calc(100vh - 365.5px - ${hasErrorBanner ? '121px' : '0px'} - 64px)`,
                  xl: `calc(100vh - 365.5px - ${hasErrorBanner ? '121px' : '0px'} - 64px)`,
                },
                ...(!isMobile && !isTablet && { minHeight: '300px' }),
                width: '100%',
                marginBottom: '4rem',
              }}
            >
              <GridConfirmationDeleteModal />
              {isMobile ? <MobileDataGrid /> : <DesktopDataGrid<T, TErrors> />}
            </Box>
          </Box>

          <CreateEditSidebar />
        </>
      </DataGridContextProvider>
    </>
  );
};

const GenericDataGridAndSideForm = <T, TErrors>(props: {
  data: T[];
  metadata?: PageMetadata;
  rowCount?: number;
  rowIdName?: string;
  isLoading: boolean;
  isError: boolean;
  unauthorized: boolean;
  fetchData: () => unknown;
  BuildColumns: () => GridColDef[];
  CreateComponent: React.ComponentType<{
    currentObject: T;
    resetObject: () => void;
    setSuccessMessage: (message: string) => void;
    errorMessage: string;
    setErrorMessage: (message: string) => void;
    errors: TErrors;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setErrors: (obj: any) => void;
    mode: string;
  }>;
  translationKey: string;
  endpoint: string;
  sentryMsg: string;
  cacheKey: string;
  pageDescription?: string;
  nav?: NavigationBreadcrumbItem[];
  addButtonNavigationPath?: string;
  mobileRowMainContent: React.ComponentType<{ data: T }>;
  mobileRowExpandableContent?: React.ComponentType<{
    data: T;
  }>;
  columnVisibility?: Record<string, boolean>;
  columnGroups?: ColumnGroup[];
}) => {
  const {
    data,
    metadata,
    isLoading,
    isError,
    fetchData,
    BuildColumns,
    CreateComponent,
    translationKey,
    endpoint,
    sentryMsg,
    cacheKey,
    addButtonNavigationPath = '0',
    mobileRowMainContent,
    mobileRowExpandableContent,
    columnVisibility,
    columnGroups,
    ...genericProps
  } = props;

  const [resizeToken, setResizeToken] = useState<number>(0);
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => setResizeToken((prevToken) => prevToken + 1), 100);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <DataGridConfigContextProvider<T, TErrors>
      key={resizeToken}
      data={data}
      fetchData={fetchData}
      isLoading={isLoading}
      isError={isError}
      metadata={metadata}
      translationKey={translationKey}
      addButtonNavigationPath={addButtonNavigationPath}
      endpoint={endpoint}
      cacheKey={cacheKey}
      sentryMsg={sentryMsg}
      MobileRowMainContent={mobileRowMainContent}
      MobileRowExpandableContent={mobileRowExpandableContent}
      BuildColumns={BuildColumns}
      CreateComponent={CreateComponent}
      columnVisibility={columnVisibility}
      columnGroups={columnGroups}
    >
      <GenericDataGridAndSideFormContent {...genericProps} />
    </DataGridConfigContextProvider>
  );
};

export default GenericDataGridAndSideForm;
